import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {TournamentService} from '../../app/services/tournament.service';
import {SiteService} from '../../app/services/site.service';
import {animate, group, query, stagger, style, transition, trigger} from '@angular/animations';
import posthog from 'posthog-js';
import KeenSlider, {KeenSliderInstance} from 'keen-slider';
import {Native} from '../../native';

interface GuideSlide {
  id: string;
  screenshotImage: string;
  href?: string;
  /** When set, the slide is hidden after the end of this calendar day. Default: none (no expiry). */
  expiresAt?: Date | null;
}

@Component({
  selector: 'main-guide',
  templateUrl: './main-guide-modal.component.html',
  styleUrls: ['./main-guide-modal.component.scss'],
  providers: [TournamentService, SiteService],
  animations: [
    trigger('animation', [
      transition('* <=> *', [
        group([
          query(':enter', [
            style({opacity: 0, transform: 'scale(0.7)'}),
            stagger(100, [
              animate('500ms ease-in', style({opacity: 1, transform: 'scale(1)'}))
            ])
          ], {optional: true}),
          query(':enter', [
            style({backgroundColor: 'rgba(0 ,255, 0, 0.2)'}),
            animate('3s ease-in', style({backgroundColor: 'rgba(0, 0, 0, 0.1)'}))
          ], {optional: true}),
          query(':enter h2, :enter dl', [
            style({color: 'green'}),
            animate('3s ease-in', style({color: 'inherit'}))
          ], {optional: true}),
        ]),
        group([
          query(':leave', [
            style({opacity: 1, transform: 'scale(1)'}),
            stagger(-100, [
              animate('1s ease-in', style({opacity: 0, transform: 'scale(0.7)'}))
            ])
          ], {optional: true}),
          query(':leave', [
            animate('250ms ease-in', style({backgroundColor: 'rgba(255, 0, 0, 0.2)'}))
          ], {optional: true}),
          query(':leave h2, :leave dl', [
            animate('250ms ease-in', style({color: 'red'}))
          ], {optional: true}),
        ])
      ])
    ]),
    trigger(
      'inRightOutLeft',
      [
        transition(
          ':enter',
          [
            style({left: '-105%', opacity: 0}),
            animate('0.4s  ease-out',
              style({left: 0, opacity: 1}))
          ]
        ),
        transition(
          ':leave',
          [
            style({left: 0, opacity: 1}),
            animate('0.2s ease-in',
              style({left: '-105%', opacity: 0}))
          ]
        )
      ]
    ),
    trigger(
      'inLeftOutRight',
      [
        transition(
          ':enter',
          [
            style({right: '-105%', opacity: 0}),
            animate('0.2s ease-out',
              style({right: 0, opacity: 1}))
          ]
        ),
        transition(
          ':leave',
          [
            style({right: 0, opacity: 1}),
            animate('0.4s ease-in',
              style({right: '-105%', opacity: 0}))
          ]
        )
      ]
    ),
  ]
})
export class MainGuideComponent implements OnInit {
  @ViewChild('sliderRef') sliderRef: ElementRef<HTMLDivElement>;

  public page = 1;
  public active: boolean = false;
  public dontShowAgain = false;

  currentSlide: number = 0;
  dotHelper: Array<Number> = [];
  slider: KeenSliderInstance = null;
  additionalSlides: GuideSlide[] = [];

  constructor(private host: ElementRef<HTMLElement>) {
    const shouldShowBasedOnStorage = (() => {
      const info = JSON.parse(localStorage.getItem('dontShowHomeModal') || 'null');
      if (!info || !info.date) {
        return true;
      }
      const today = new Date(2026, 9, 7).setHours(0, 0, 0, 0);
      return info.date < today; // Set to true if checkbox was checked before October 7, 2026
    })();

    const allSlides: GuideSlide[] = [
      {
        id: 'home-bsop',
        screenshotImage: 'assets/guide/home/home_bsop_compressed.png',
        href: 'https://www.instagram.com/p/Dd1fKpijHHi/',
        expiresAt: new Date(2026, 10, 3), // 03/11/2026
      }
    ];

    this.additionalSlides = allSlides.filter(slide => !this.isSlideExpired(slide));
    this.active = shouldShowBasedOnStorage && this.additionalSlides.length > 0;
  }

  private isSlideExpired(slide: GuideSlide, now = new Date()): boolean {
    if (slide.expiresAt == null) {
      return false;
    }
    const endOfExpiryDay = new Date(slide.expiresAt);
    endOfExpiryDay.setHours(23, 59, 59, 999);
    return now > endOfExpiryDay;
  }

  async ngOnInit() {
    // Keen Slider listens for click in the capture phase and, after a drag of a
    // few pixels, calls stopPropagation. That happens before the anchor handler
    // and the document listener, so the banner never reaches Native.openExternal.
    this.host.nativeElement.addEventListener('click', this.onGuideLinkClick, true);
  }

  next() {
    this.page++;
  }

  previous() {
    if (this.page > 1) {
      this.page--;
    }
  }

  close() {
    this.active = false;
    if (this.dontShowAgain) {
      const now = new Date();
      const info = {
        dont_show: true,
        date: now.getTime()
      };
      localStorage.setItem('dontShowHomeModal', JSON.stringify(info));
    } else if (localStorage.getItem('dontShowHomeModal')) {
      localStorage.removeItem('dontShowHomeModal');
    }
  }

  private readonly onGuideLinkClick = (event: MouseEvent) => {
    if (event.button !== 0) {
      return;
    }

    const target = event.target as Element | null;
    const anchor = target?.closest?.('a.banner-link') as HTMLAnchorElement | null;
    if (!anchor || !this.host.nativeElement.contains(anchor)) {
      return;
    }

    const href = anchor.getAttribute('href');
    if (!href) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    posthog.capture('user_clicked_guide');
    void Native.openExternal(href);
  };

  ngAfterViewInit() {
    if (this.additionalSlides.length === 0 || !this.sliderRef?.nativeElement) {
      return;
    }

    this.slider = new KeenSlider(this.sliderRef.nativeElement, {
        initial: this.currentSlide,
        loop: this.additionalSlides.length > 1,
        slideChanged: (s) => {
          this.currentSlide = s.track.details.rel;
        },
      },
      this.additionalSlides.length > 1 ? [
        (slider) => {
          let timeout;
          let mouseOver = false;

          function clearNextTimeout() {
            clearTimeout(timeout);
          }

          function nextTimeout() {
            clearTimeout(timeout);
            if (mouseOver) {
              return;
            }
            timeout = setTimeout(() => {
              slider.next();
            }, 7000);
          }

          slider.on('created', () => {
            slider.container.addEventListener('mouseover', () => {
              mouseOver = true;
              clearNextTimeout();
            });
            slider.container.addEventListener('mouseout', () => {
              mouseOver = false;
              nextTimeout();
            });
            nextTimeout();
          });
          slider.on('dragStarted', clearNextTimeout);
          slider.on('animationEnded', nextTimeout);
          slider.on('updated', nextTimeout);
        },
      ] : [],
    );
    
    if (this.additionalSlides.length > 1) {
      this.slider.moveToIdx(Math.floor(Math.random() * this.slider.slides.length));
    }

    this.dotHelper = [
      ...Array(this.slider.track.details.slides.length).keys(),
    ];
  }

  ngOnDestroy() {
    this.host.nativeElement.removeEventListener('click', this.onGuideLinkClick, true);
    if (this.slider) {
      this.slider.destroy();
    }
  }
}